import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import Header from '../components/ui/Header';
import AuthenticationGuard from '../components/ui/AuthenticationGuard';
import Icon from '../components/AppIcon';
import Button from '../components/ui/Button';
import useAuth from '../hooks/useAuth';
import Input from '../components/ui/Input';
import { createFlashcardDeck, fetchFlashcardDeckSummaries } from '../services/flashcardsService';
import SmartLoadingBuffer from '../components/ui/SmartLoadingBuffer';

const FlashcardsPage = () => {
  const navigate = useNavigate();
  const { user, initializing } = useAuth();
  const [decks, setDecks] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formSuccess, setFormSuccess] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    tags: '',
    color: ''
  });

  const loadDecks = useCallback(async () => {
    if (!user?.id) {
      setDecks([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const summaries = await fetchFlashcardDeckSummaries(user.id);
      setDecks(summaries);
    } catch (err) {
      console.error('Failed to load flashcard decks:', err);
      setError(err);
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!initializing) {
      loadDecks();
    }
  }, [initializing, loadDecks]);

  const decksForDisplay = useMemo(() => {
    return decks.map((deck) => {
      const lastStudiedLabel = deck.lastStudiedAt
        ? formatDistanceToNow(new Date(deck.lastStudiedAt), { addSuffix: true })
        : 'Not studied yet';

      return {
        ...deck,
        lastStudiedLabel,
      };
    });
  }, [decks]);

  const stats = useMemo(() => {
    if (!decks?.length) {
      return {
        studiedToday: 0,
        mastered: 0,
        deckCount: 0,
        totalCards: 0,
      };
    }

    return {
      studiedToday: decks.reduce((sum, deck) => sum + (deck.studiedToday ?? 0), 0),
      mastered: decks.reduce((sum, deck) => sum + (deck.mastered ?? 0), 0),
      deckCount: decks.length,
      totalCards: decks.reduce((sum, deck) => sum + (deck.cardCount ?? 0), 0),
    };
  }, [decks]);

  const showEmptyState = !error && decksForDisplay.length === 0;

  const getDifficultyColor = useCallback((difficulty) => {
    switch (difficulty) {
      case 'Beginner':
        return 'text-success bg-success/10';
      case 'Intermediate':
        return 'text-warning bg-warning/10';
      case 'Advanced':
        return 'text-destructive bg-destructive/10';
      default:
        return 'text-muted-foreground bg-muted/20';
    }
  }, []);

  const handleFormChange = useCallback((event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setFormError(null);
    setFormSuccess(null);
  }, []);

  const handleCreateDeck = useCallback(async (event) => {
    event.preventDefault();

    if (!user?.id) {
      setFormError('You need to be signed in to create a deck.');
      return;
    }

    if (!formData.name?.trim()) {
      setFormError('Give your deck a name so you can find it later.');
      return;
    }

    setIsSubmitting(true);
    setFormError(null);

    try {
      const newDeck = await createFlashcardDeck({
        userId: user.id,
        name: formData.name,
        description: formData.description,
        tags: formData.tags,
        color: formData.color
      });

      setFormSuccess('Deck created. Add cards and start reviewing!');
      setFormData({ name: '', description: '', tags: '', color: '' });
      setShowCreateForm(false);
      setDecks((prev) => [newDeck, ...prev]);
      if (newDeck?.id) {
        navigate(`/flashcards/${newDeck.id}`);
      }
    } catch (err) {
      console.error('Failed to create deck:', err);
      setFormError(err?.message || 'We could not create your deck. Try again.');
    } finally {
      setIsSubmitting(false);
    }
  }, [formData, user?.id]);

  if (initializing || isLoading) {
    return (
      <AuthenticationGuard>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-16">
            <SmartLoadingBuffer
              className="min-h-[60vh]"
              title="Building your study stacks"
              message="Were fetching decks, cards, and recent sessions to tailor your review."
              hint="Cogito is arranging your flashcards for optimal recall."
            />
          </div>
        </div>
      </AuthenticationGuard>
    );
  }

  return (
    <AuthenticationGuard>
      <div className="min-h-screen bg-background">
        <Header />

        <main className="pt-16">
          <div className="max-w-6xl mx-auto px-4 lg:px-6 py-8">
            {error && (
              <div className="mb-8 p-4 border border-destructive/40 bg-destructive/5 text-destructive rounded-lg">
                <p className="font-semibold">We couldn’t load your flashcard decks.</p>
                <p className="text-sm opacity-80 mt-1">{error.message || 'Please try again later.'}</p>
              </div>
            )}

            {/* Page Header */}
            <div className="mb-8">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">Flashcards</h1>
                  <p className="text-muted-foreground">Review and master your study materials</p>
                </div>
                <Button
                  className="hidden sm:flex"
                  onClick={() => {
                    setShowCreateForm(true);
                    setFormError(null);
                    setFormSuccess(null);
                  }}
                >
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create Deck
                </Button>
              </div>
            </div>

            {formSuccess && (
              <div className="mb-6 rounded-lg border border-success/20 bg-success/10 p-4 text-sm text-success">
                {formSuccess}
              </div>
            )}

            {showCreateForm && (
              <div className="mb-8 bg-card border border-border rounded-xl p-6">
                <div className="flex items-start space-x-3 mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Layers" size={18} className="text-primary" />
                  </div>
                  <div className="flex-1">
                    <h2 className="text-lg font-semibold text-foreground">Create a new deck</h2>
                    <p className="text-sm text-muted-foreground">Organize related concepts into decks so spaced repetition keeps them fresh.</p>
                  </div>
                </div>

                {formError && (
                  <div className="mb-4 rounded-lg border border-error/20 bg-error/10 p-3 text-sm text-error">
                    {formError}
                  </div>
                )}

                <form className="space-y-6" onSubmit={handleCreateDeck}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Deck name</span>
                      <Input
                        name="name"
                        value={formData.name}
                        onChange={handleFormChange}
                        placeholder="e.g., JavaScript fundamentals"
                        required
                      />
                    </label>
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Tags</span>
                      <Input
                        name="tags"
                        value={formData.tags}
                        onChange={handleFormChange}
                        placeholder="Comma separated (e.g., coding, functions)"
                      />
                    </label>
                  </div>

                  <label className="flex flex-col space-y-2">
                    <span className="text-sm font-medium text-foreground">Description</span>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleFormChange}
                      className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 min-h-[100px]"
                      placeholder="Summarize what this deck will help you remember."
                    />
                  </label>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <label className="flex flex-col space-y-2">
                      <span className="text-sm font-medium text-foreground">Accent color</span>
                      <Input
                        name="color"
                        value={formData.color}
                        onChange={handleFormChange}
                        placeholder="Optional hex or theme color"
                      />
                    </label>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:items-center sm:space-x-3 space-y-3 sm:space-y-0">
                    <Button type="submit" disabled={isSubmitting} iconName="Save">
                      {isSubmitting ? 'Saving...' : 'Create deck'}
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      iconName="X"
                      onClick={() => {
                        setShowCreateForm(false);
                        setFormData({ name: '', description: '', tags: '', color: '' });
                        setFormError(null);
                        setFormSuccess(null);
                      }}
                    >
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}

            {/* Study Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                    <Icon name="Brain" size={24} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.studiedToday}</p>
                    <p className="text-sm text-muted-foreground">Studied Today</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-success/10 rounded-lg flex items-center justify-center">
                    <Icon name="CheckCircle" size={24} className="text-success" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.mastered}</p>
                    <p className="text-sm text-muted-foreground">Cards Mastered</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-accent/10 rounded-lg flex items-center justify-center">
                    <Icon name="BookOpen" size={24} className="text-accent" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.deckCount}</p>
                    <p className="text-sm text-muted-foreground">Active Decks</p>
                  </div>
                </div>
              </div>

              <div className="bg-card rounded-xl border border-border p-6">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-warning/10 rounded-lg flex items-center justify-center">
                    <Icon name="Zap" size={24} className="text-warning" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-foreground">{stats.totalCards}</p>
                    <p className="text-sm text-muted-foreground">Total Cards</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Flashcard Decks */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {showEmptyState ? (
                <div className="md:col-span-2 lg:col-span-3">
                  <div className="bg-muted/20 border border-dashed border-border rounded-xl p-10 text-center space-y-3">
                    <Icon name="BookOpen" size={28} className="mx-auto text-muted-foreground" />
                    <div>
                      <p className="font-semibold text-foreground">No decks yet</p>
                      <p className="text-sm text-muted-foreground">Create your first deck to start studying with flashcards.</p>
                    </div>
                    <Button
                      onClick={() => {
                        setShowCreateForm(true);
                        setFormError(null);
                        setFormSuccess(null);
                      }}
                    >
                      <Icon name="Plus" size={16} className="mr-2" />
                      Create Deck
                    </Button>
                  </div>
                </div>
              ) : (
                decksForDisplay.map((deck) => (
                  <div
                    key={deck?.id}
                    className="bg-card rounded-xl border border-border p-6 hover:shadow-sm transition-shadow cursor-pointer"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="BookOpen" size={24} className="text-primary" />
                      </div>
                      <span className={`text-xs px-2 py-1 rounded-full ${getDifficultyColor(deck?.difficultyLabel)}`}>
                        {deck?.difficultyLabel}
                      </span>
                    </div>

                    <div className="mb-4">
                      <h3 className="text-lg font-semibold text-foreground mb-2">
                        {deck?.name}
                      </h3>
                      <p className="text-sm text-muted-foreground">
                        {deck?.description}
                      </p>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium text-foreground">
                          {deck?.mastered}/{deck?.cardCount}
                        </span>
                      </div>

                      <div className="w-full bg-muted rounded-full h-2">
                        <div
                          className="bg-primary h-2 rounded-full transition-all duration-300"
                          style={{ width: `${deck?.cardCount ? Math.min(100, (deck.mastered / deck.cardCount) * 100) : 0}%` }}
                        ></div>
                      </div>

                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">
                          Category: {deck?.category}
                        </span>
                        <span className="text-muted-foreground">
                          {deck?.lastStudiedLabel}
                        </span>
                      </div>

                      {deck?.studiedToday > 0 && (
                        <div className="flex items-center space-x-1 text-xs text-success">
                          <Icon name="CheckCircle" size={12} />
                          <span>{deck?.studiedToday} studied today</span>
                        </div>
                      )}
                    </div>

                    <div className="mt-4 pt-4 border-t border-border">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full"
                        onClick={() => deck?.id && navigate(`/flashcards/${deck.id}`)}
                      >
                        <Icon name="Play" size={14} className="mr-2" />
                        Study Now
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Mobile Create Button */}
            <div className="sm:hidden fixed bottom-6 right-6">
              <Button
                size="lg"
                className="w-14 h-14 rounded-full shadow-lg"
                onClick={() => {
                  setShowCreateForm(true);
                  setFormError(null);
                  setFormSuccess(null);
                }}
              >
                <Icon name="Plus" size={24} />
              </Button>
            </div>
          </div>
        </main>
      </div>
    </AuthenticationGuard>
  );
};

export default FlashcardsPage;